import {
  DisplayStatusEnum,
  InvoiceStatus,
  LangCode,
  OrderStatus,
  QuoteStatus,
  TimeUnit,
} from 'api-client'
import { ErrorEnum as ValidationErrorType } from 'api-order-client'
import { RoleType, UserStatus } from 'api-user-client'
import React from 'react'

import { SupportedLanguage } from '../utils/enums'

export { ValidationErrorType }

import { DangerousGoodsCode } from '../components/GHSSymbols/GHSSymbols'
import { en } from './en'
import { SortingType } from './sorting'
import { TFn } from './translations'

export enum TranslationClass {
  AccountStatus = 'AccountStatus',
  DangerousGoodsClass = 'DangerousGoodsClass',
  DateFilterOption = 'DateFilterOption',
  InvoiceStatus = 'invoiceStatus',
  OrderStatus = 'Order.Status',
  QuoteStatus = 'QuoteStatus',
  SortingLabel = 'SortingLabel',
  TimeUnit = 'TimeUnit',
  UserRole = 'UserRole',
  ValidationError = 'ValidationError',
}

export type MakeGeneric<Type extends Record<string, unknown>> = {
  [key in keyof Type]: Type[key] extends Record<string, unknown>
    ? MakeGeneric<Type[key]> // eslint-disable-next-line @typescript-eslint/no-explicit-any
    : Type[key] extends (...a: any[]) => string
    ? Type[key]
    : Type[key] extends string[]
    ? string[]
    : string
}

export type QuoteStatusType =
  | keyof typeof QuoteStatus
  | keyof typeof DisplayStatusEnum
export type InvoiceStatusType = keyof typeof InvoiceStatus & {
  description: string
}
export type OrderStatusType = keyof typeof OrderStatus
export type UserStatusType = keyof typeof UserStatus & { description: string }
export type UserRole = keyof typeof RoleType & { description: string }
export type DateFilterOptionType =
  | 'lastWeek'
  | 'lastMonth'
  | 'last3Months'
  | 'lastYear'

export type TranslationMap = MakeGeneric<typeof en>
export type TranslationKey = keyof TranslationMap

export enum ValidationWarningType {
  PriceChanged = 'price-changed',
  PriceMismatch = 'price-mismatch',
}

export const fillMissingTranslationsWithDefaults = (
  translation: Partial<TranslationMap>,
) => Object.assign(en, translation)

export const createTranslationFunction = <Keys>(classKey: TranslationClass) => (
  translationFn: TFn,
  key: Keys | undefined,
): TranslationMap[TranslationKey] =>
  translationFn(
    (key ? `${classKey.toLowerCase()}.${key}` : undefined) as TranslationKey,
  )

export const getLocalizedOrderStatus = createTranslationFunction<OrderStatusType>(
  TranslationClass.OrderStatus,
)

export const getLocalizedInvoiceStatus = createTranslationFunction<InvoiceStatusType>(
  TranslationClass.InvoiceStatus,
)

export const getLocalizedDateFilter = createTranslationFunction<DateFilterOptionType>(
  TranslationClass.DateFilterOption,
)

export const getLocalizedQuoteStatus = createTranslationFunction<
  Omit<QuoteStatusType, QuoteStatus.InProgress>
>(TranslationClass.QuoteStatus)

export const getLocalizedSorting = createTranslationFunction<SortingType>(
  TranslationClass.SortingLabel,
)

export const getLocalizedValidationError = createTranslationFunction<
  ValidationErrorType | ValidationWarningType
>(TranslationClass.ValidationError)

export const getLocalizedAccountStatus = createTranslationFunction<
  UserStatusType | UserStatus
>(TranslationClass.AccountStatus)

export const getLocalizedUserRoles = createTranslationFunction<
  UserRole | RoleType
>(TranslationClass.UserRole)

export const getLocalizedDangerousGoodsClass = createTranslationFunction<DangerousGoodsCode>(
  TranslationClass.DangerousGoodsClass,
)

export const getLocalizedTimeUnit = (
  translationFn: TFn,
  timeUnit: TimeUnit | undefined,
): string => {
  if (!timeUnit) return 'Unknown'
  const classKey = TranslationClass.TimeUnit.toLowerCase()
  const oneUnit = translationFn(
    `${classKey}.one.${timeUnit.unit.toLowerCase()}` as TranslationKey,
  ) as string
  const manyUnit = translationFn(
    `${classKey}.many.${timeUnit.unit.toLowerCase()}` as TranslationKey,
  ) as string

  return timeUnit.quantity === 1
    ? `${timeUnit.quantity} ${oneUnit}`
    : `${timeUnit.quantity} ${manyUnit}`
}

export const getLocalizedLanguage = (
  translationFn: TFn,
  isoCode: LangCode | SupportedLanguage | undefined,
) => {
  if (!isoCode) return 'Unknown'
  return translationFn(`isolanguage.${isoCode}` as TranslationKey)
}

// This function allows you to replace part of a translation and optionally wrap it with a component
// Replacement string wrapper needs to be able to accept children
export const wrapStringWithElement = (
  string: string,
  stringToBeReplaced: string,
  replacementString: string,
  replacementStringWrapper?: React.ReactElement,
) => {
  const startIndex = string.indexOf(stringToBeReplaced)
  const parsedTranslation = {
    after: string.slice(startIndex + stringToBeReplaced.length, string.length),
    before: string.slice(0, startIndex),
  }

  if (replacementStringWrapper !== undefined) {
    return React.createElement(
      React.Fragment,
      null,
      parsedTranslation.before,
      React.cloneElement(
        replacementStringWrapper,
        undefined,
        replacementString,
      ),
      parsedTranslation.after,
    )
  }

  return `${parsedTranslation.before}${replacementString}${parsedTranslation.after}`
}

// This uses the function above but instead allows you to pass a translation key
export const replaceStringInTranslation = (
  translationFn: TFn,
  key: TranslationKey,
  stringToBeReplaced: string,
  replacementString: string,
  replacementStringWrapper?: React.ReactElement,
) => {
  const translation = translationFn(key)
  if (typeof translation === 'string') {
    return wrapStringWithElement(
      translation,
      stringToBeReplaced,
      replacementString,
      replacementStringWrapper,
    )
  }
}
